import * as THREE from 'three';

export interface AmbientDustConfig {
  bounds: {
    center: THREE.Vector3;
    innerRadius: number;
    outerRadius: number;
    height: number;
  };
  particleCount: number;
  qualityLevel: 'low' | 'medium' | 'high' | 'ultra';
}

export interface DustParticle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  size: number;
  opacity: number;
  rotationSpeed: number;
  orbitAngle: number;
  orbitRadius: number;
  orbitSpeed: number;
}

export class AmbientDust extends THREE.Group {
  private config: AmbientDustConfig;
  
  // Dust particles
  private dustParticles: DustParticle[] = [];
  private particleGeometry: THREE.BufferGeometry;
  private particleMaterial: THREE.PointsMaterial;
  private particleSystem: THREE.Points;
  
  // Buffer arrays
  private positions: Float32Array;
  private sizes: Float32Array;
  private opacities: Float32Array;
  
  // Visual properties
  private dustColor: THREE.Color = new THREE.Color(0x6688aa);
  private baseOpacity: number = 0.15;
  private baseSize: number = 0.3;
  
  // Animation
  private time: number = 0;
  private congestionLevel: 'low' | 'medium' | 'high' | 'critical' = 'low';
  
  // Glow effects
  private coreGlow: {
    intensity: number;
    radius: number;
    color: THREE.Color;
    pulsation: number;
  } = {
    intensity: 0.5,
    radius: 8,
    color: new THREE.Color(0x88aaff),
    pulsation: 0.1,
  };
  
  // Energy arcs (for high congestion)
  private energyArcs: {
    frequency: number;
    intensity: number;
    duration: number;
    color: THREE.Color;
  } = {
    frequency: 0.1,
    intensity: 0.8,
    duration: 2.0,
    color: new THREE.Color(0xaaffff),
  };
  
  private arcSystem: THREE.Group;
  private activeArcs: THREE.Object3D[] = [];
  
  constructor(config: AmbientDustConfig) {
    super();
    
    this.config = config;
    this.userData = { id: 'ambient-dust', type: 'ambient' };
    
    this.createDustParticles();
    this.createParticleSystem();
    this.createCoreGlow();
    this.createArcSystem();
  }
  
  private createDustParticles(): void {
    const { bounds, particleCount } = this.config;
    
    for (let i = 0; i < particleCount; i++) {
      // Random position within bounds
      const radius = bounds.innerRadius + Math.random() * (bounds.outerRadius - bounds.innerRadius);
      const angle = Math.random() * Math.PI * 2;
      const height = (Math.random() - 0.5) * bounds.height;
      
      const position = new THREE.Vector3(
        Math.cos(angle) * radius,
        height,
        Math.sin(angle) * radius
      );
      
      // Very slow random movement
      const velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 0.1,
        (Math.random() - 0.5) * 0.05,
        (Math.random() - 0.5) * 0.1
      );
      
      const particle: DustParticle = {
        position,
        velocity,
        size: this.baseSize * (0.5 + Math.random() * 0.5),
        opacity: this.baseOpacity * (0.3 + Math.random() * 0.7),
        rotationSpeed: (Math.random() - 0.5) * 0.1,
        orbitAngle: angle,
        orbitRadius: radius,
        orbitSpeed: 0.05 + Math.random() * 0.05,
      };
      
      this.dustParticles.push(particle);
    }
  }
  
  private createParticleSystem(): void {
    const particleCount = this.dustParticles.length;
    
    // Create buffer arrays
    this.positions = new Float32Array(particleCount * 3);
    this.sizes = new Float32Array(particleCount);
    this.opacities = new Float32Array(particleCount);
    
    // Create geometry
    this.particleGeometry = new THREE.BufferGeometry();
    this.particleGeometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.particleGeometry.setAttribute('size', new THREE.BufferAttribute(this.sizes, 1));
    this.particleGeometry.setAttribute('opacity', new THREE.BufferAttribute(this.opacities, 1));
    
    // Create material
    this.particleMaterial = new THREE.PointsMaterial({
      color: this.dustColor,
      size: this.baseSize,
      transparent: true,
      opacity: this.baseOpacity,
      blending: THREE.AdditiveBlending,
      depthTest: true,
      depthWrite: false,
      sizeAttenuation: true,
    });
    
    // Create points system
    this.particleSystem = new THREE.Points(this.particleGeometry, this.particleMaterial);
    this.particleSystem.userData = { id: 'dust-particles', type: 'points' };
    
    this.add(this.particleSystem);
    
    // Initial buffer update
    this.updateParticleBuffers();
  }
  
  private createCoreGlow(): void {
    // Create a glowing sphere at the center
    const glowGeometry = new THREE.SphereGeometry(this.coreGlow.radius, 16, 8);
    
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: this.coreGlow.color,
      transparent: true,
      opacity: this.coreGlow.intensity * 0.3,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
    });
    
    const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
    glowMesh.userData = { id: 'core-glow', type: 'glow' };
    
    this.add(glowMesh);
  }
  
  private createArcSystem(): void {
    this.arcSystem = new THREE.Group();
    this.arcSystem.userData = { id: 'energy-arcs', type: 'effects' };
    this.add(this.arcSystem);
  }
  
  private updateParticleBuffers(): void {
    for (let i = 0; i < this.dustParticles.length; i++) {
      const particle = this.dustParticles[i];
      
      // Position
      const posIndex = i * 3;
      this.positions[posIndex] = particle.position.x;
      this.positions[posIndex + 1] = particle.position.y;
      this.positions[posIndex + 2] = particle.position.z;
      
      // Size
      this.sizes[i] = particle.size;
      
      // Opacity
      this.opacities[i] = particle.opacity;
    }
    
    // Update buffer attributes
    this.particleGeometry.attributes['position'].needsUpdate = true;
    this.particleGeometry.attributes['size'].needsUpdate = true;
    this.particleGeometry.attributes['opacity'].needsUpdate = true;
  }
  
  public update(deltaTime: number): void {
    this.time += deltaTime;
    
    this.updateDustParticles(deltaTime);
    this.updateCoreGlow(deltaTime);
    this.updateEnergyArcs(deltaTime);
    this.updateParticleBuffers();
  }
  
  private updateDustParticles(deltaTime: number): void {
    const { bounds } = this.config;
    const congestionMultiplier = this.getCongestionMultiplier();
    
    for (const particle of this.dustParticles) {
      // Update orbital motion
      particle.orbitAngle += particle.orbitSpeed * deltaTime * congestionMultiplier;
      
      // Calculate new orbital position
      const orbitX = Math.cos(particle.orbitAngle) * particle.orbitRadius;
      const orbitZ = Math.sin(particle.orbitAngle) * particle.orbitRadius;
      
      // Add slow drift
      particle.position.add(particle.velocity.clone().multiplyScalar(deltaTime));
      
      // Blend orbital motion with current position
      const orbitTarget = new THREE.Vector3(orbitX, particle.position.y, orbitZ);
      particle.position.lerp(orbitTarget, 0.02 * deltaTime);
      
      // Keep particles within bounds
      const distance = Math.sqrt(particle.position.x ** 2 + particle.position.z ** 2);
      if (distance > bounds.outerRadius) {
        particle.position.normalize().multiplyScalar(bounds.outerRadius * 0.9);
      }
      
      if (Math.abs(particle.position.y) > bounds.height * 0.5) {
        particle.position.y = Math.sign(particle.position.y) * bounds.height * 0.5 * 0.9;
      }
      
      // Update opacity based on congestion
      const targetOpacity = this.baseOpacity * (0.5 + congestionMultiplier * 0.5);
      particle.opacity += (targetOpacity - particle.opacity) * deltaTime;
      
      // Subtle size pulsing
      const pulse = 1 + Math.sin(this.time * 0.5 + particle.orbitAngle) * 0.1;
      particle.size = this.baseSize * pulse * (0.8 + congestionMultiplier * 0.4);
    }
  }
  
  private updateCoreGlow(deltaTime: number): void {
    const coreGlow = this.children.find(child => child.userData['id'] === 'core-glow');
    if (!coreGlow || !(coreGlow instanceof THREE.Mesh)) return;
    
    const material = coreGlow.material as THREE.MeshBasicMaterial;
    const congestionMultiplier = this.getCongestionMultiplier();
    
    // Pulsating intensity
    const pulsation = 1 + Math.sin(this.time * this.coreGlow.pulsation * 2) * 0.2;
    const targetOpacity = this.coreGlow.intensity * pulsation * (0.3 + congestionMultiplier * 0.7);
    
    material.opacity += (targetOpacity - material.opacity) * deltaTime * 2;
    
    // Scale pulsing
    const scale = 1 + Math.sin(this.time * this.coreGlow.pulsation) * 0.1 * congestionMultiplier;
    coreGlow.scale.setScalar(scale);
  }
  
  private updateEnergyArcs(deltaTime: number): void {
    // Update existing arcs
    this.activeArcs = this.activeArcs.filter(arc => {
      arc.userData['lifetime'] -= deltaTime;
      if (arc.userData['lifetime'] <= 0) {
        this.arcSystem.remove(arc);
        if (arc instanceof THREE.Mesh) {
          arc.geometry.dispose();
          if (arc.material) {
            if (Array.isArray(arc.material)) {
              arc.material.forEach(mat => mat.dispose());
            } else {
              arc.material.dispose();
            }
          }
        }
        return false;
      }
      
      // Fade out over time
      if (arc instanceof THREE.Mesh && arc.material instanceof THREE.MeshBasicMaterial) {
        const lifeProgress = arc.userData['lifetime'] / this.energyArcs.duration;
        arc.material.opacity = lifeProgress * this.energyArcs.intensity;
      }
      
      return true;
    });
    
    // Spawn new arcs during high congestion
    if (this.congestionLevel === 'high' || this.congestionLevel === 'critical') {
      const spawnChance = this.energyArcs.frequency * deltaTime;
      if (Math.random() < spawnChance) {
        this.createEnergyArc();
      }
    }
  }
  
  private createEnergyArc(): void {
    const { bounds } = this.config;
    
    // Create arc between two random points
    const startAngle = Math.random() * Math.PI * 2;
    const endAngle = startAngle + (Math.random() - 0.5) * Math.PI;
    
    const startRadius = bounds.innerRadius + Math.random() * (bounds.outerRadius - bounds.innerRadius);
    const endRadius = bounds.innerRadius + Math.random() * (bounds.outerRadius - bounds.innerRadius);
    
    const startPos = new THREE.Vector3(
      Math.cos(startAngle) * startRadius,
      (Math.random() - 0.5) * bounds.height * 0.5,
      Math.sin(startAngle) * startRadius
    );
    
    const endPos = new THREE.Vector3(
      Math.cos(endAngle) * endRadius,
      (Math.random() - 0.5) * bounds.height * 0.5,
      Math.sin(endAngle) * endRadius
    );
    
    // Create curve for the arc
    const curve = new THREE.QuadraticBezierCurve3(
      startPos,
      startPos.clone().add(endPos).multiplyScalar(0.5).add(
        new THREE.Vector3(0, bounds.height * 0.2, 0)
      ),
      endPos
    );
    
    // Create geometry from curve
    const points = curve.getPoints(16);
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    
    const material = new THREE.LineBasicMaterial({
      color: this.energyArcs.color,
      transparent: true,
      opacity: this.energyArcs.intensity,
      blending: THREE.AdditiveBlending,
    });
    
    const arc = new THREE.Line(geometry, material);
    arc.userData = {
      id: `energy-arc-${this.time}`,
      type: 'arc',
      lifetime: this.energyArcs.duration,
    };
    
    this.arcSystem.add(arc);
    this.activeArcs.push(arc);
  }
  
  private getCongestionMultiplier(): number {
    const multipliers = {
      low: 0.2,
      medium: 0.5,
      high: 0.8,
      critical: 1.0,
    };
    return multipliers[this.congestionLevel];
  }
  
  public updateCongestion(level: 'low' | 'medium' | 'high' | 'critical'): void {
    this.congestionLevel = level;
    
    // Update dust color based on congestion
    const colors = {
      low: new THREE.Color(0x6688aa),
      medium: new THREE.Color(0x88aa66),
      high: new THREE.Color(0xaaaa44),
      critical: new THREE.Color(0xaa6644),
    };
    
    this.dustColor = colors[level];
    this.particleMaterial.color.copy(this.dustColor);
    
    // Update core glow color
    const glowColors = {
      low: new THREE.Color(0x88aaff),
      medium: new THREE.Color(0x88ffaa),
      high: new THREE.Color(0xffaa88),
      critical: new THREE.Color(0xff8888),
    };
    
    this.coreGlow.color = glowColors[level];
    
    const coreGlow = this.children.find(child => child.userData['id'] === 'core-glow');
    if (coreGlow instanceof THREE.Mesh && coreGlow.material instanceof THREE.MeshBasicMaterial) {
      coreGlow.material.color.copy(this.coreGlow.color);
    }
  }
  
  public setParticleCount(count: number): void {
    if (count === this.dustParticles.length) return;
    
    if (count > this.dustParticles.length) {
      // Add more particles
      const toAdd = count - this.dustParticles.length;
      for (let i = 0; i < toAdd; i++) {
        // Create new particle similar to initial creation
        const { bounds } = this.config;
        const radius = bounds.innerRadius + Math.random() * (bounds.outerRadius - bounds.innerRadius);
        const angle = Math.random() * Math.PI * 2;
        const height = (Math.random() - 0.5) * bounds.height;
        
        const position = new THREE.Vector3(
          Math.cos(angle) * radius,
          height,
          Math.sin(angle) * radius
        );
        
        const velocity = new THREE.Vector3(
          (Math.random() - 0.5) * 0.1,
          (Math.random() - 0.5) * 0.05,
          (Math.random() - 0.5) * 0.1
        );
        
        const particle: DustParticle = {
          position,
          velocity,
          size: this.baseSize * (0.5 + Math.random() * 0.5),
          opacity: this.baseOpacity * (0.3 + Math.random() * 0.7),
          rotationSpeed: (Math.random() - 0.5) * 0.1,
          orbitAngle: angle,
          orbitRadius: radius,
          orbitSpeed: 0.05 + Math.random() * 0.05,
        };
        
        this.dustParticles.push(particle);
      }
    } else {
      // Remove particles
      this.dustParticles = this.dustParticles.slice(0, count);
    }
    
    // Recreate particle system with new count
    this.remove(this.particleSystem);
    this.particleSystem.geometry.dispose();
    if (Array.isArray(this.particleSystem.material)) {
      this.particleSystem.material.forEach(mat => mat.dispose());
    } else {
      this.particleSystem.material.dispose();
    }
    
    this.createParticleSystem();
  }
  
  public setQualityLevel(level: 'low' | 'medium' | 'high' | 'ultra'): void {
    this.config.qualityLevel = level;
    
    // Adjust visual quality
    const qualitySettings = {
      low: {
        opacity: this.baseOpacity * 0.5,
        size: this.baseSize * 0.7,
        enableArcs: false,
      },
      medium: {
        opacity: this.baseOpacity * 0.8,
        size: this.baseSize * 0.9,
        enableArcs: true,
      },
      high: {
        opacity: this.baseOpacity,
        size: this.baseSize,
        enableArcs: true,
      },
      ultra: {
        opacity: this.baseOpacity * 1.2,
        size: this.baseSize * 1.1,
        enableArcs: true,
      },
    };
    
    const settings = qualitySettings[level];
    this.particleMaterial.opacity = settings.opacity;
    
    // Update arc system visibility
    this.arcSystem.visible = settings.enableArcs;
  }
  
  public dispose(): void {
    // Dispose particle system
    this.particleGeometry.dispose();
    this.particleMaterial.dispose();
    
    // Dispose all arc materials and geometries
    this.arcSystem.traverse((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });
    
    // Dispose core glow
    const coreGlow = this.children.find(child => child.userData['id'] === 'core-glow');
    if (coreGlow instanceof THREE.Mesh) {
      coreGlow.geometry.dispose();
      if (coreGlow.material instanceof THREE.Material) {
        coreGlow.material.dispose();
      }
    }
    
    // Clear arrays
    this.dustParticles = [];
    this.activeArcs = [];
  }
}